import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import type { ManufacturerOption } from '../../inspectionReport/hooks/useHomeServiceManufacturers';
import { useGet, usePost, usePut } from '../../../sharedModules/hooks/useQuery';
import { resolveApiAssetUrl } from '../../../sharedModules/utils/apiAssetUrl';
import {
  EQUIPMENT_IMAGE_ACCEPTED_MIME_TYPES,
  EQUIPMENT_IMAGE_MAX_BYTES,
} from '../constants/equipmentImageUploadLimits';

const SECTIONS = ['hvac', 'plumbing', 'electrical', 'water_treatment', 'air_quality'] as const;
type Section = (typeof SECTIONS)[number];

const SECTION_LABELS: Record<Section, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  water_treatment: 'Water Treatment',
  air_quality: 'Air Quality',
};

const EQUIPMENT_TYPES: Partial<Record<Section, { value: string; label: string }[]>> = {
  hvac: [
    { value: 'split_system', label: 'Split System' },
    { value: 'boiler', label: 'Boiler' },
    { value: 'mini_split', label: 'Mini-Split' },
    { value: 'package_unit', label: 'Package Unit' },
  ],
  plumbing: [
    { value: 'tank_water_heater', label: 'Water Heater' },
    { value: 'tankless_water_heater', label: 'Tankless Water Heater' },
  ],
};

const SECTION_U_TRADE: Record<Section, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  water_treatment: 'Water Treatment',
  air_quality: '',
};

export interface EquipmentImageRecord {
  id: number;
  section: string;
  equipment_type: string | null;
  manufacturer_description: string | null;
  image_url: string | null;
  has_image: boolean;
}

interface ManufacturersResponseBody {
  result: ManufacturerOption[];
}

interface EquipmentImageUploadDialogProps {
  open: boolean;
  section: Section;
  record?: EquipmentImageRecord;
  onClose: () => void;
  onSaved: () => void;
}

const EquipmentImageUploadDialog: React.FC<EquipmentImageUploadDialogProps> = ({
  open,
  section,
  record,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(record);
  const [equipmentType, setEquipmentType] = useState(record?.equipment_type ?? '');
  const [manufacturerDescription, setManufacturerDescription] = useState<string>(
    record?.manufacturer_description ?? '',
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(record?.image_url ?? null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const typeOptions = EQUIPMENT_TYPES[section] ?? [];
  const uTrade = SECTION_U_TRADE[section];

  const { data: manufacturersAxios } = useGet<ManufacturersResponseBody>({
    url: '/home_services/manufacturers',
    params: { u_trade: uTrade },
    enabled: open && Boolean(uTrade),
  });
  const manufacturers = manufacturersAxios?.data?.result ?? [];

  const createImage = usePost();
  const updateImage = usePut();
  const saving = createImage.isPending || updateImage.isPending;

  React.useEffect(() => {
    if (!open) return;
    setEquipmentType(record?.equipment_type ?? '');
    setManufacturerDescription(record?.manufacturer_description ?? '');
    setImageFile(null);
    setPreview(record?.image_url ?? null);
    setError(null);
  }, [open, record]);

  const validateImageFile = (file: File): string | null => {
    const allowed = EQUIPMENT_IMAGE_ACCEPTED_MIME_TYPES as readonly string[];
    if (!file.type || !allowed.includes(file.type)) {
      return 'Only JPEG or PNG images are allowed.';
    }
    if (file.size > EQUIPMENT_IMAGE_MAX_BYTES) {
      return `Image must be ${EQUIPMENT_IMAGE_MAX_BYTES / (1024 * 1024)}MB or smaller.`;
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      setImageFile(null);
      setPreview(record?.image_url ?? null);
      e.target.value = '';
      return;
    }
    setError(null);
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!imageFile && !isEdit) {
      setError('Please select an image file.');
      return;
    }
    setError(null);
    try {
      const formData = new FormData();
      formData.append('inspection_equipment_image[section]', section);
      if (equipmentType) formData.append('inspection_equipment_image[equipment_type]', equipmentType);
      if (manufacturerDescription !== '')
        formData.append('inspection_equipment_image[manufacturer_description]', manufacturerDescription);
      if (imageFile) formData.append('inspection_equipment_image[image]', imageFile);

      if (isEdit && record) {
        await updateImage.mutateAsync({
          url: `/home_services/inspection_equipment_images/${record.id}`,
          data: formData,
        });
      } else {
        await createImage.mutateAsync({
          url: '/home_services/inspection_equipment_images',
          data: formData,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const msgs = err?.response?.data?.errors;
      setError(Array.isArray(msgs) ? msgs.join(', ') : 'Failed to save image.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Equipment Image' : 'Add Equipment Image'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth variant="filled" disabled>
            <InputLabel>Section</InputLabel>
            <Select value={section}>
              <MenuItem value={section}>{SECTION_LABELS[section]}</MenuItem>
            </Select>
          </FormControl>

          {typeOptions.length > 0 && (
            <FormControl fullWidth variant="filled">
              <InputLabel>Equipment Type (optional — leave blank for section default)</InputLabel>
              <Select value={equipmentType} onChange={(e) => setEquipmentType(e.target.value as string)}>
                <MenuItem value="">— Any type (section default) —</MenuItem>
                {typeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {uTrade && (
            <FormControl fullWidth variant="filled">
              <InputLabel>Manufacturer (optional — leave blank for type/section default)</InputLabel>
              <Select
                value={manufacturerDescription}
                onChange={(e) => setManufacturerDescription(e.target.value as string)}
              >
                <MenuItem value="">— Any manufacturer (type/section default) —</MenuItem>
                {manufacturers.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box>
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()} size="small">
              {imageFile ? 'Change Image' : 'Select Image'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {imageFile && (
              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                {imageFile.name}
              </Typography>
            )}
          </Box>

          {preview && (
            <Box
              component="img"
              src={resolveApiAssetUrl(preview) ?? ''}
              alt="Preview"
              sx={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain', borderRadius: 1, border: '1px solid #ddd' }}
            />
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentImageUploadDialog;
